/* global PAGE_STATUS, PAGE_META, EVENT_META */

import ApplicationController from "application_controller.js";
import * as Turbo from "@hotwired/turbo";

export default class extends ApplicationController {

  debug = false;

  connect() {
    this.log_function("connect", this.identifier); //* this.identifier is useful for debugging multiple instances


    if (PAGE_STATUS === 404) { history.replaceState({}, "", "/"); } //* Change browser URL to "/" without reloading

    //* Back button / history handling — store bound reference so disconnect can remove it
    this.boundHandlePopState = this.handlePopState.bind(this);
    window.addEventListener("popstate", this.boundHandlePopState);

    //* Retry pending modal on reconnect
    this._onlineHandler = this.retryPendingModal.bind(this);
    window.addEventListener("online", this._onlineHandler);
  }

  disconnect() {
    this.log_function("disconnect");

    window.removeEventListener("popstate", this.boundHandlePopState);
    window.removeEventListener("online", this._onlineHandler);
  }

  //* Handle modal link clicks
  handleClick(event) {
    this.log_function("handleClick");

    event.preventDefault();

    const link = event.currentTarget;
    const url = new URL(link.href, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);

    //* Merge current page params into link URL without duplicating
    currentParams.forEach((value, key) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    this._pendingModalUrl = url.toString();
    history.pushState({ modal: true }, "", url.pathname + url.search);

    //* Only show skeleton if load takes longer than 250ms
    this._skeletonTimer = setTimeout(() => this.showSkeletonModal(), 250);

    //* Attempt to load the modal content
    this.loadModalContent(url.toString());
  }

  async loadModalContent(url) {
    try {
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html, text/html" },
      });

      if (response.ok) {
        const html = await response.text();

        //* Cancel skeleton if it hasn't shown yet
        clearTimeout(this._skeletonTimer);

        const frame = document.getElementById("event_modal");
        if (!frame) return;

        const existingModal = frame.querySelector(".modal");

        if (existingModal) {
          //* Skeleton is visible — swap content in place (same modal, same backdrop)
          const temp = document.createElement("div");
          temp.innerHTML = html;

          //* Extract content from turbo-stream template if present
          const template = temp.querySelector("turbo-stream template");
          const content = template ? template.content : temp;

          //* Execute script to update EVENT_META global
          const script = content.querySelector("script");
          if (script) new Function(script.textContent)();

          //* Replace dialog content inside the existing modal
          const newModal = content.querySelector(".modal");
          const newDialog = (newModal || content).querySelector(".modal-dialog");
          if (newDialog) {
            existingModal.querySelector(".modal-dialog").replaceWith(newDialog);
          }

          //* Update id from real modal
          if (newModal) {
            existingModal.id = newModal.id;
          }

          //* Update metadata with real event data
          window.setMetadata(EVENT_META);
        } else {
          //* Fast path — no skeleton, inject full content (fades in via modal controller)
          frame.innerHTML = html;
        }

        this._pendingModalUrl = null;
      }
    } catch {
      //* Fetch failed (offline) — show skeleton immediately if not already visible
      clearTimeout(this._skeletonTimer);
      const frame = document.getElementById("event_modal");
      if (frame && !frame.querySelector(".modal")) {
        this.showSkeletonModal();
      }
      this.dispatch("offline", { bubbles: true });
    }
  }

  retryPendingModal() {
    if (!this._pendingModalUrl) return;
    this.log_function("retryPendingModal");

    this.loadModalContent(this._pendingModalUrl);
  }

  showSkeletonModal() {
    const template = document.getElementById("event_modal_skeleton");
    if (!template) return;

    const frame = document.getElementById("event_modal");
    if (!frame) return;

    const clone = template.content.cloneNode(true);
    frame.innerHTML = "";
    frame.appendChild(clone);
  }

  shouldOpenModal(path) {
    this.log_function("shouldOpenModal");

    //* Example: modal route is `/events/:id`
    return /^\/events\/\d+$/.test(path);
  }

  openModal(url) {
    this.log_function("openModal");

    window.setMetadata(EVENT_META); //* EVENT_META is global

    const modal = document.getElementById("event_modal");

    if (modal && (modal.src !== url)) {
      Turbo.visit(url, { frame: "event_modal" });
    }
  }

  closeModal() {
    this.log_function("closeModal");

    this._pendingModalUrl = null;
    window.setMetadata(PAGE_META); //* PAGE_META is global

    const modal = document.getElementById("event_modal");

    if (modal) {
      modal.innerHTML = "";
      modal.src = "";
    }
  }

  handlePopState(event) {
    this.log_function("handlePopState");

    const url = window.location.pathname;

    if (this.shouldOpenModal(url)) {
      Turbo.visit(url, { frame: "event_modal" });
    } else {
      this.closeModal();
    }
  }
}
